import { merge, Stack } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cancelIcon from '../../../assets/icons/svg/cancel.svg';
import padlockRedIcon from '../../../assets/icons/svg/padlockRed.svg';
import saveIcon from '../../../assets/icons/svg/save.svg';
import userRedIcon from '../../../assets/icons/svg/userRed.svg';
import { AccessType, useAuth } from '../../../contexts/authContext';
import { useNotificationsManager } from '../../../contexts/notificationContext';
import { ResponsiveSize, useResponsiveSize } from '../../../contexts/responsiveContext';
import { useUser, useUserManager } from '../../../contexts/userContext';
import { Data } from '../../../data/Data';
import Page from '../../Page';
import DropdownSituationActuelle from './DropdownSituationActuelle';
import DropdownSituationCivile from './DropdownSituationCivile';
import FormActionButton from './FormActionButton';
import PasswordForm from './PasswordForm';
import PickerYear from './PickerYear';
import Preferences from './Preferences';
import ProfileDatePicker from './ProfileDatePicker';
import ProfileTextField from './ProfileTextField';

const MonProfil = () => {

    // CONTEXTS
    const { accessType } = useAuth();
    const { confirm, dismiss, extractErrorMessages, inform } = useNotificationsManager();
    const { responsiveSize } = useResponsiveSize();
    const { user } = useUser();
    const { updateUser } = useUserManager();

    // MEMOS
    const {
        // Client
        adresse = "",
        civilite = "",
        complementAdresse = "",
        consentementCollecteDonneesPersonnelles = false,
        consentementDiffusionDonneesPersonnelles = false,
        cp = "",
        email = "",
        loginContact = "",
        nom = "",
        nomDeJeuneFille = "",
        pays = "",
        prenom = "",
        souhaiteRecevoirOffres = false,
        tel1 = "",
        tel2 = "",
        ville = "",

        // Prospect
        dateMariage = undefined,
        datenaissance = undefined,
        dateNaissanceEnfant1 = undefined,
        dateNaissanceEnfant2 = undefined,
        dateNaissanceEnfant3 = undefined,
        dateNaissanceEnfant4 = undefined,
        dateNaissanceEnfant5 = undefined,
        lieuNaissance = "",
        nationalite = "",
        nomEmployeur = "",
        profession = "",
        revenusAnnuels = "",
        situationActuelle = undefined,
        situationCivile = undefined
    } = useMemo(() => user, [user]);

    // STATES
    const [_emailHasChanged, { setFalse: _setEmailHasntChangedYet, setTrue: _setEmailHasChanged }] = useBoolean(true);
    const [_isEmailValid, { setFalse: _setEmailIsNotValid, setTrue: _setEmailIsValid }] = useBoolean(true);
    const [_isProfileEditionEnabled, { toggle: _toggleProfileEdition }] = useBoolean(false);
    const [_isPasswordEditionEnabled, { toggle: _togglePasswordEdition }] = useBoolean(false);

    // Client
    const [_newAdresse, _setNewAdresse] = useState(adresse);
    const [_newComplementAdresse, _setNewComplementAdresse] = useState(complementAdresse);
    const [_newConsentementDiffusionDonneesPersonnelles, _setNewConsentementDiffusionDonneesPersonnelles] = useState(consentementDiffusionDonneesPersonnelles);
    const [_newCp, _setNewCp] = useState(cp);
    const [_newEmail, _setNewEmail] = useState(email);
    const [_newPays, _setNewPays] = useState(pays);
    const [_newSouhaiteRecevoirOffres, _setNewSouhaiteRecevoirOffres] = useState(souhaiteRecevoirOffres);
    const [_newTel1, _setNewTel1] = useState(tel1);
    const [_newTel2, _setNewTel2] = useState(tel2);
    const [_newVille, _setNewVille] = useState(ville);

    // Prospect
    const [_anneesNaissancesEnfants, _setAnneesNaissancesEnfants] = useState([]);
    const [_newConsentementCollecteDonneesPersonnelles, _setNewConsentementCollecteDonneesPersonnelles] = useState(consentementCollecteDonneesPersonnelles);
    const [_newDateMariage, _setNewDateMariage] = useState(dateMariage);
    const [_newDateNaissance, _setNewDateNaissance] = useState(datenaissance);
    const [_newDateNaissanceEnfant1, _setNewDateNaissanceEnfant1] = useState(dateNaissanceEnfant1);
    const [_newDateNaissanceEnfant2, _setNewDateNaissanceEnfant2] = useState(dateNaissanceEnfant2);
    const [_newDateNaissanceEnfant3, _setNewDateNaissanceEnfant3] = useState(dateNaissanceEnfant3);
    const [_newDateNaissanceEnfant4, _setNewDateNaissanceEnfant4] = useState(dateNaissanceEnfant4);
    const [_newDateNaissanceEnfant5, _setNewDateNaissanceEnfant5] = useState(dateNaissanceEnfant5);
    const [_newLieuNaissance, _setNewLieuNaissance] = useState(lieuNaissance);
    const [_newNationalite, _setNewNationalite] = useState(nationalite);
    const [_newNomEmployeur, _setNewNomEmployeur] = useState(nomEmployeur);
    const [_newProfession, _setNewProfession] = useState(profession);
    const [_newRevenusAnnuels, _setNewRevenusAnnuels] = useState(revenusAnnuels);
    const [_newSituationActuelle, _setNewSituationActuelle] = useState(situationActuelle);
    const [_newSituationCivile, _setNewSituationCivile] = useState(situationCivile);

    // MEMOS
    const _styles = useMemo(
        () => {
            let styles = {
                buttons: {
                    root: {
                        flexDirection: "column",
                        gap: 13
                    }
                },
                column: {
                    root: {
                        gap: 3,
                        flexBasis: 1,
                        flexGrow: 1,
                    }
                },
                fields: {
                    root: {
                        flexDirection: "column",
                        gap: 3
                    }
                },
            };

            if (responsiveSize >= ResponsiveSize.medium)
                styles = merge(
                    styles,
                    {
                        buttons: {
                            root: {
                                flexBasis: 1,
                                flexDirection: "row",
                                flexGrow: 1,
                            }
                        },
                        fields: {
                            root: {
                                flexBasis: 1,
                                flexDirection: "row",
                                flexGrow: 1,
                                gap: "0 5%"
                            }
                        },
                    }
                );

            return styles;
        },
        [responsiveSize]
    );

    // CALLBACKS
    const _checkEmail = useCallback(() => {
        const id = inform("Vérification de l'email en cours...");
        Data._checkEmail(_newEmail).then(response => {
            if (response.codeRetour === 0) _setEmailIsValid();
            else _setEmailIsNotValid();
            dismiss(id);
        });
    }, [_newEmail, _setEmailIsNotValid, _setEmailIsValid, dismiss, inform]);

    const _saveProfile = useCallback(() => {
        const id = inform("Mise à jour du profil en cours...");

        let contact = {
            adresse: _newAdresse,
            complementAdresse: _newComplementAdresse,
            consentementCollecteDonneesPersonnelles: true, // Pas indispensable, sécurité supplémentaire
            consentementDiffusionDonneesPersonnelles: _newConsentementDiffusionDonneesPersonnelles,
            cp: _newCp,
            email: _newEmail,
            pays: _newPays,
            souhaiteRecevoirOffres: _newSouhaiteRecevoirOffres,
            tel1: _newTel1,
            tel2: _newTel2,
            ville: _newVille
        }
        if (accessType === AccessType.prospect)
            contact = {
                ...contact,
                consentementCollecteDonneesPersonnelles: _newConsentementCollecteDonneesPersonnelles,
                dateMariage: _newDateMariage,
                datenaissance: _newDateNaissance,
                dateNaissanceEnfant1: _newDateNaissanceEnfant1,
                dateNaissanceEnfant2: _newDateNaissanceEnfant2,
                dateNaissanceEnfant3: _newDateNaissanceEnfant3,
                dateNaissanceEnfant4: _newDateNaissanceEnfant4,
                dateNaissanceEnfant5: _newDateNaissanceEnfant5,
                lieuNaissance: _newLieuNaissance,
                nationalite: _newNationalite,
                nomEmployeur: _newNomEmployeur,
                profession: _newProfession,
                revenusAnnuels: _newRevenusAnnuels,
                situationActuelle: _newSituationActuelle,
                situationCivile: _newSituationCivile
            };

        Data._updateProfile(contact).then(response => {
            if (response.codeRetour === 0) {
                updateUser(contact);
                _toggleProfileEdition();
                confirm("Mise à jour effectuée avec succès");
            }
            else extractErrorMessages(response);
            dismiss(id);
        });
    }, [accessType, confirm, dismiss, extractErrorMessages, inform, _newAdresse, _newComplementAdresse, _newConsentementCollecteDonneesPersonnelles, _newConsentementDiffusionDonneesPersonnelles, _newCp, _newDateMariage, _newDateNaissance, _newDateNaissanceEnfant1, _newDateNaissanceEnfant2, _newDateNaissanceEnfant3, _newDateNaissanceEnfant4, _newDateNaissanceEnfant5, _newEmail, _newLieuNaissance, _newNationalite, _newNomEmployeur, _newPays, _newProfession, _newRevenusAnnuels, _newSituationActuelle, _newSituationCivile, _newSouhaiteRecevoirOffres, _newTel1, _newTel2, _newVille, _toggleProfileEdition, updateUser]);

    const _renderField = useCallback((field) => {
        const { isEditionAllowed, isInvalid, id, key, label, onBlur, onChange, onSelectDate, selectedKey, value } = field;
        switch (key) {
            case "dateMariage":
            case "datenaissance":
                return <ProfileDatePicker
                    id={id}
                    key={key}
                    datePickerProps={{
                        disabled: !isEditionAllowed || !_isProfileEditionEnabled,
                        label,
                        onSelectDate,
                        value
                    }}
                />;
            case "situationActuelle":
                return <DropdownSituationActuelle
                    id={id}
                    key={key}
                    dropdownProps={{
                        disabled: !isEditionAllowed || !_isProfileEditionEnabled,
                        onChange,
                        selectedKey
                    }}
                />;
            case "situationCivile":
                return <DropdownSituationCivile
                    id={id}
                    key={key}
                    dropdownProps={{
                        disabled: !isEditionAllowed || !_isProfileEditionEnabled,
                        onChange,
                        selectedKey
                    }}
                />;
            case "anneesNaissancesEnfants":
                return <PickerYear
                    key={key}
                    label={label}
                    pickerProps={{
                        disabled: !isEditionAllowed || !_isProfileEditionEnabled,
                        id,
                        onChange
                    }}
                    years={_anneesNaissancesEnfants}
                />;
            default:
                return <ProfileTextField
                    key={key}
                    textFieldProps={{
                        disabled: !isEditionAllowed || !_isProfileEditionEnabled,
                        id,
                        isInvalid,
                        label,
                        onBlur: onBlur || (_ => null),
                        onChange: onChange || (_ => null),
                        value: value || ""
                    }}
                />;
        }
    }, [_anneesNaissancesEnfants, _isProfileEditionEnabled]);

    // EFFECTS
    useEffect(() => _setEmailHasntChangedYet(), [_isProfileEditionEnabled, _setEmailHasntChangedYet]);
    useEffect(() => _setEmailIsValid(), [_isProfileEditionEnabled, _setEmailIsValid]);
    useEffect(() => _setNewAdresse(adresse), [_isProfileEditionEnabled, adresse]);
    useEffect(() => _setNewComplementAdresse(complementAdresse), [_isProfileEditionEnabled, complementAdresse]);
    useEffect(() => _setNewConsentementDiffusionDonneesPersonnelles(consentementDiffusionDonneesPersonnelles), [_isProfileEditionEnabled, consentementDiffusionDonneesPersonnelles]);
    useEffect(() => _setNewCp(cp), [_isProfileEditionEnabled, cp]);
    useEffect(() => _setNewEmail(email), [_isProfileEditionEnabled, email]);
    useEffect(() => _setNewPays(pays), [_isProfileEditionEnabled, pays]);
    useEffect(() => _setNewSouhaiteRecevoirOffres(souhaiteRecevoirOffres), [_isProfileEditionEnabled, souhaiteRecevoirOffres]);
    useEffect(() => _setNewTel1(tel1), [_isProfileEditionEnabled, tel1]);
    useEffect(() => _setNewTel2(tel2), [_isProfileEditionEnabled, tel2]);
    useEffect(() => _setNewVille(ville), [_isProfileEditionEnabled, ville]);
    useEffect(() => _setNewConsentementCollecteDonneesPersonnelles(consentementCollecteDonneesPersonnelles), [_isProfileEditionEnabled, consentementCollecteDonneesPersonnelles]);
    useEffect(() => _setNewDateMariage(dateMariage), [_isProfileEditionEnabled, dateMariage]);
    useEffect(() => _setNewDateNaissance(datenaissance), [_isProfileEditionEnabled, datenaissance]);
    useEffect(() => _setNewDateNaissanceEnfant1(dateNaissanceEnfant1), [_isProfileEditionEnabled, dateNaissanceEnfant1]);
    useEffect(() => _setNewDateNaissanceEnfant2(dateNaissanceEnfant2), [_isProfileEditionEnabled, dateNaissanceEnfant2]);
    useEffect(() => _setNewDateNaissanceEnfant3(dateNaissanceEnfant3), [_isProfileEditionEnabled, dateNaissanceEnfant3]);
    useEffect(() => _setNewDateNaissanceEnfant4(dateNaissanceEnfant4), [_isProfileEditionEnabled, dateNaissanceEnfant4]);
    useEffect(() => _setNewDateNaissanceEnfant5(dateNaissanceEnfant5), [_isProfileEditionEnabled, dateNaissanceEnfant5]);
    useEffect(() => _setNewLieuNaissance(lieuNaissance), [_isProfileEditionEnabled, lieuNaissance]);
    useEffect(() => _setNewNationalite(nationalite), [_isProfileEditionEnabled, nationalite]);
    useEffect(() => _setNewNomEmployeur(nomEmployeur), [_isProfileEditionEnabled, nomEmployeur]);
    useEffect(() => _setNewProfession(profession), [_isProfileEditionEnabled, profession]);
    useEffect(() => _setNewRevenusAnnuels(revenusAnnuels), [_isProfileEditionEnabled, revenusAnnuels]);
    useEffect(() => _setNewSituationActuelle(situationActuelle), [_isProfileEditionEnabled, situationActuelle]);
    useEffect(() => _setNewSituationCivile(situationCivile), [_isProfileEditionEnabled, situationCivile]);

    useEffect(() => {
        let newAnneesNaissancesEnfants = [];
        if (!!_newDateNaissanceEnfant1) newAnneesNaissancesEnfants.push(new Date(_newDateNaissanceEnfant1).getFullYear());
        if (!!_newDateNaissanceEnfant2) newAnneesNaissancesEnfants.push(new Date(_newDateNaissanceEnfant2).getFullYear());
        if (!!_newDateNaissanceEnfant3) newAnneesNaissancesEnfants.push(new Date(_newDateNaissanceEnfant3).getFullYear());
        if (!!_newDateNaissanceEnfant4) newAnneesNaissancesEnfants.push(new Date(_newDateNaissanceEnfant4).getFullYear());
        if (!!_newDateNaissanceEnfant5) newAnneesNaissancesEnfants.push(new Date(_newDateNaissanceEnfant5).getFullYear());
        _setAnneesNaissancesEnfants(newAnneesNaissancesEnfants);
    }, [_newDateNaissanceEnfant1, _newDateNaissanceEnfant2, _newDateNaissanceEnfant3, _newDateNaissanceEnfant4, _newDateNaissanceEnfant5]);

    // UI
    const _fieldsCommunsA = useMemo(() => ([
        {
            id: "tfCivilite",
            isEditionAllowed: false,
            key: "civilite",
            label: "civilité :",
            value: civilite
        }, {
            id: "tfNom",
            isEditionAllowed: false,
            key: "nom",
            label: "nom :",
            value: nom
        }, {
            id: "tfNomDeJeuneFille",
            isEditionAllowed: false,
            key: "nomDeJeuneFille",
            label: "nom de jeune fille :",
            value: nomDeJeuneFille
        }, {
            id: "tfPrenom",
            isEditionAllowed: false,
            key: "prenom",
            label: "prénom :",
            value: prenom
        }, {
            id: "tfLoginContact",
            isEditionAllowed: false,
            key: "loginContact",
            label: "mon login :",
            value: loginContact
        }, {
            id: "tfTel1",
            isEditionAllowed: true,
            key: "tel1",
            label: "tél. mobile :",
            onChange: (event, newValue) => _setNewTel1(newValue),
            value: _newTel1
        }, {
            id: "tfTel2",
            isEditionAllowed: true,
            key: "tel2",
            label: "tél. domicile :",
            onChange: (event, newValue) => _setNewTel2(newValue),
            value: _newTel2
        },
    ]), [_newTel1, _newTel2, civilite, loginContact, nom, nomDeJeuneFille, prenom]);

    const _fieldsCommunsB = useMemo(() => ([
        {
            id: "tfEmail",
            isEditionAllowed: true,
            isInvalid: !_isEmailValid,
            key: "email",
            label: "email :",
            onBlur: () => _emailHasChanged ? _checkEmail() : null,
            onChange: (event, newValue) => {

                newValue = newValue.trim();
                const splitted = newValue.split("@");
                if (splitted.length > 2) return;

                let localPart, domain;
                if (splitted.length === 2) { // On considère que la localPart n'existe qu'à partir du moment où l'on a saisi l'arobase
                    localPart = splitted[0];
                    domain = splitted[1];
                }

                // Tests sur la localPart
                if (!!localPart) {
                    if (localPart.startsWith(".")) return;
                    if (localPart.startsWith("-")) return;
                    if (localPart.split("..").length > 1) return;
                    if (localPart.length > 64) return;
                }

                // Tests sur le domaine
                if (!!domain) {
                    if (domain.startsWith("-")) return;
                    if (domain.split("..").length > 1) return;
                }

                _setNewEmail(newValue);
                _setEmailHasChanged();

            },
            value: _newEmail
        }, {
            id: "tfAdresse",
            isEditionAllowed: true,
            key: "adresse",
            label: "adresse :",
            onChange: (event, newValue) => _setNewAdresse(newValue),
            value: _newAdresse
        }, {
            id: "tfComplementAdresse",
            isEditionAllowed: true,
            key: "complementAdresse",
            label: "complément d'adresse :",
            onChange: (event, newValue) => _setNewComplementAdresse(newValue),
            value: _newComplementAdresse
        }, {
            id: "tfCp",
            isEditionAllowed: true,
            key: "cp",
            label: "code postal :",
            onChange: (event, newValue) => _setNewCp(newValue),
            value: _newCp
        }, {
            id: "tfVille",
            isEditionAllowed: true,
            key: "ville",
            label: "ville :",
            onChange: (event, newValue) => _setNewVille(newValue),
            value: _newVille
        }, {
            id: "tfPays",
            isEditionAllowed: true,
            key: "pays",
            label: "pays :",
            onChange: (event, newValue) => _setNewPays(newValue),
            value: _newPays
        }
    ]), [_checkEmail, _emailHasChanged, _isEmailValid, _newAdresse, _newComplementAdresse, _newCp, _newEmail, _newPays, _newVille, _setEmailHasChanged]);

    const _fieldsProspect = useMemo(() => ([
        {
            id: "tfNationalite",
            isEditionAllowed: true,
            key: "nationalite",
            label: "nationalité :",
            onChange: (event, newValue) => _setNewNationalite(newValue),
            value: _newNationalite
        }, {
            id: "dpDateNaissance",
            isEditionAllowed: true,
            key: "datenaissance",
            label: "date de naissance :",
            onSelectDate: (date) => _setNewDateNaissance(date),
            value: _newDateNaissance
        }, {
            id: "tfLieuNaissance",
            isEditionAllowed: true,
            key: "lieuNaissance",
            label: "lieu de naissance :",
            onChange: (event, newValue) => _setNewLieuNaissance(newValue),
            value: _newLieuNaissance
        }, {
            id: "dpDateMariage",
            isEditionAllowed: true,
            key: "dateMariage",
            label: "date de mariage :",
            onSelectDate: (date) => _setNewDateMariage(date),
            value: _newDateMariage
        }, {
            isEditionAllowed: true,
            key: "anneesNaissancesEnfants",
            label: "années de naissances des enfants :",
            onChange: (years) => {
                _setNewDateNaissanceEnfant1(years[0] ? new Date(years[0], 1, 1) : null);
                _setNewDateNaissanceEnfant2(years[1] ? new Date(years[1], 1, 1) : null);
                _setNewDateNaissanceEnfant3(years[2] ? new Date(years[2], 1, 1) : null);
                _setNewDateNaissanceEnfant4(years[3] ? new Date(years[3], 1, 1) : null);
                _setNewDateNaissanceEnfant5(years[4] ? new Date(years[4], 1, 1) : null);
            }
        }, {
            id: "tfProfession",
            isEditionAllowed: true,
            key: "profession",
            label: "profession :",
            onChange: (event, newValue) => _setNewProfession(newValue),
            value: _newProfession
        }, {
            id: "tfNomEmployeur",
            isEditionAllowed: true,
            key: "nomEmployeur",
            label: "nom employeur :",
            onChange: (event, newValue) => _setNewNomEmployeur(newValue),
            value: _newNomEmployeur
        }, {
            id: "tfRevenusAnnuels",
            isEditionAllowed: true,
            key: "revenusAnnuels",
            label: "revenus annuels :",
            onChange: (event, newValue) => _setNewRevenusAnnuels(newValue),
            value: _newRevenusAnnuels
        }, {
            id: "ddSituationActuelle",
            isEditionAllowed: true,
            key: "situationActuelle",
            label: "situation actuelle :",
            onChange: (event, option) => _setNewSituationActuelle(option.key),
            value: _newSituationActuelle
        }, {
            id: "ddSituationCivile",
            isEditionAllowed: true,
            key: "situationCivile",
            label: "situation civile :",
            onChange: (event, option) => _setNewSituationCivile(option.key),
            value: _newSituationCivile
        }
    ]), [_newDateMariage, _newDateNaissance, _newLieuNaissance, _newNationalite, _newNomEmployeur, _newProfession, _newRevenusAnnuels, _newSituationActuelle, _newSituationCivile]);

    const _fieldsLeftCol = useMemo(() => {
        return accessType >= AccessType.restricted
            ? _fieldsCommunsA
            : [..._fieldsCommunsA, ..._fieldsCommunsB];
    }, [accessType, _fieldsCommunsA, _fieldsCommunsB]);

    const _fieldsRightCol = useMemo(() => {
        return accessType >= AccessType.restricted
            ? _fieldsCommunsB
            : _fieldsProspect;
    }, [accessType, _fieldsCommunsB, _fieldsProspect]);

    // RENDER
    return <Page title="Mon profil">

        {_isPasswordEditionEnabled
            ? <PasswordForm onCancel={_togglePasswordEdition} onSave={_togglePasswordEdition} />
            : <>
                <Stack styles={_styles.fields}>

                    <Stack styles={_styles.column}>
                        {_fieldsLeftCol.map((field) => _renderField(field))}
                    </Stack>

                    <Stack styles={_styles.column}>
                        {_fieldsRightCol.map((field) => _renderField(field))}
                    </Stack>

                </Stack>

                <Preferences
                    consentementCollecteDonneesPersonnelles={_newConsentementCollecteDonneesPersonnelles}
                    consentementDiffusionDonneesPersonnelles={_newConsentementDiffusionDonneesPersonnelles}
                    enabled={_isProfileEditionEnabled}
                    onChangeConsentementCollecteDonneesPersonnelles={(event, checked) => _setNewConsentementCollecteDonneesPersonnelles(accessType === AccessType.prospect ? checked : true)} // Pas indispensable, sécurité supplémentaire
                    onChangeConsentementDiffusionDonneesPersonnelles={(event, checked) => _setNewConsentementDiffusionDonneesPersonnelles(checked)}
                    onChangeSouhaiteRecevoirOffres={(event, checked) => _setNewSouhaiteRecevoirOffres(checked)}
                    souhaiteRecevoirOffres={_newSouhaiteRecevoirOffres}
                />

                <Stack styles={_styles.buttons}>
                    {_isProfileEditionEnabled && <>
                        <FormActionButton id="btnSave" src={saveIcon} text="Enregistrer mes modifications" onClick={_saveProfile} primary disabled={!_isEmailValid} />
                        <FormActionButton id="btnCancel" src={cancelIcon} text="Annuler mes modifications" onClick={_toggleProfileEdition} />
                    </>}
                    {!_isProfileEditionEnabled && <>
                        <FormActionButton id="btnEditProfile" src={userRedIcon} text="Modifier mes informations" onClick={_toggleProfileEdition} />
                        <FormActionButton id="btnEditPassword" src={padlockRedIcon} text="Modifier mon mot de passe" onClick={_togglePasswordEdition} />
                    </>}
                </Stack>
            </>
        }

    </Page>;

};

export default MonProfil;